import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from './firebase';
import { getAppointmentTypeLabel } from './appointmentTypes';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, deleteDoc } from 'firebase/firestore';

function CompleteTrade() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [authLoading, setAuthLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Timer state
  const [timeLeft, setTimeLeft] = useState(null);
  const [timerActive, setTimerActive] = useState(false);
  const timeLeftRef = useRef(null);
  const shouldClearSessionStorage = useRef(false);
  const refreshDetected = useRef(false);
  

  
  // Payment form state
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    cardholderName: '',
    expiryDate: '',
    cvv: ''
  });
  
  const { isTakingTrade, tradeOrder } = location.state || {};

  // Initialize Firebase Functions
  const functions = getFunctions();

  const transferTradingOrderCallable = httpsCallable(functions, 'transferTradingOrder');

  // Function to clean up active session
  const cleanupActiveSession = async (sessionId, orderId = null) => {
    console.log('🧹 Starting cleanupActiveSession with:', { sessionId, orderId });
    
    if (!sessionId && !orderId) {
      console.log('🧹 No sessionId or orderId provided, skipping cleanup');
      return;
    }
    
    try {
      if (sessionId) {
        // Clean up specific session
        console.log('🧹 Cleaning up specific active session:', sessionId);
        const sessionRef = doc(db, 'activeBookings', sessionId);
        console.log('🧹 Session reference created:', sessionRef.path);
        
        await deleteDoc(sessionRef);
        console.log('✅ Specific active session cleaned up successfully');
      }
      
      if (orderId) {
        // Clean up ALL sessions for this order (in case of duplicates)
        console.log('🧹 Cleaning up ALL active sessions for order:', orderId);
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        
        const sessionsQuery = query(
          collection(db, 'activeBookings'),
          where('orderId', '==', orderId),
          where('type', '==', 'trading_order_reservation')
        );
        
        console.log('🧹 Executing query for order sessions...');
        const sessionsSnapshot = await getDocs(sessionsQuery);
        console.log('🧹 Found', sessionsSnapshot.size, 'sessions for order:', orderId);
        
        const deletePromises = [];
        
        sessionsSnapshot.forEach(doc => {
          console.log('🧹 Deleting duplicate session:', doc.id);
          deletePromises.push(deleteDoc(doc.ref));
        });
        
        if (deletePromises.length > 0) {
          console.log('🧹 Executing', deletePromises.length, 'delete operations...');
          await Promise.all(deletePromises);
          console.log(`✅ Cleaned up ${deletePromises.length} active sessions for order ${orderId}`);
        } else {
          console.log('🧹 No sessions found to delete for order:', orderId);
        }
      }
      
      // Add a small delay to ensure Firestore write completes
      console.log('🧹 Adding cleanup delay...');
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('✅ Cleanup delay completed');
    } catch (error) {
      console.error('❌ Error cleaning up active session:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      // Don't throw error - session cleanup failure shouldn't block the trade
    }
  };

  // Check authentication on component mount
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthLoading(false);
      if (user) {
        setCurrentUser(user);
        console.log('✅ User authenticated for Complete Trade page:', user.uid);
        
        // Don't automatically clean up sessions when user is authenticated
        // Sessions should only be cleaned up when booking is cancelled, completed, or timer expires
        if (tradeOrder?.originalOrderId) {
          console.log('✅ User authenticated - keeping existing session for order:', tradeOrder.originalOrderId);
        }
      } else {
        console.log('❌ User not authenticated, redirecting to auth page');
        navigate('/auth', { 
          state: { 
            redirectPath: '/complete-trade',
            message: 'Please sign in to complete the trade.'
          }
        });
      }
    });

    return () => unsubscribe();
  }, [navigate, tradeOrder?.originalOrderId]);

  // Redirect if no trade order data (but not if refresh was detected)
  useEffect(() => {
    if (!authLoading && (!isTakingTrade || !tradeOrder) && !refreshDetected.current) {
      console.log('❌ No trade order data, redirecting to appointment trade');
      navigate('/appointment-swap');
    }
  }, [authLoading, isTakingTrade, tradeOrder, navigate]);

  // Handle page refresh and session end detection FIRST (before anything else)
  useEffect(() => {
    console.log('🔍 Refresh and session detection effect running...');
    console.log('🔍 authLoading:', authLoading);
    console.log('🔍 isTakingTrade:', isTakingTrade);
    console.log('🔍 tradeOrder:', tradeOrder);
    
    // Check if session has ended (user navigated away and came back)
    const sessionEnded = sessionStorage.getItem('completeTradeSessionEnded');
    if (sessionEnded === 'true') {
      console.log('🚨 Session ended detected - user navigated away and returned!');
      sessionStorage.removeItem('completeTradeSessionEnded');
      navigate('/appointment-swap', { 
        state: { 
          expired: true,
          message: 'Your booking session has ended. Please try taking the appointment again.'
        } 
      });
      return; // Exit early to prevent timer initialization
    }
    
    // Check for refresh regardless of auth loading state
    const savedTimer = sessionStorage.getItem('completeTradeTimer');
    console.log('🔍 Saved timer data:', savedTimer);
    
    if (savedTimer) {
      try {
        const timerData = JSON.parse(savedTimer);
        console.log('🔍 Parsed timer data:', timerData);
        console.log('🚨 Page refresh detected on CompleteTrade page - redirecting!');
        
        // Set flag to prevent timer initialization
        refreshDetected.current = true;
        
        // Set flag to indicate user is leaving the booking process (refresh = leaving)
        shouldClearSessionStorage.current = true;
        
        // Clean up the active session before redirecting
        if (timerData.tradeOrder?.activeSessionId) {
          console.log('🧹 Cleaning up session due to page refresh...');
          cleanupActiveSession(timerData.tradeOrder.activeSessionId, timerData.tradeOrder.originalOrderId).catch(error => {
            console.error('Error during refresh cleanup:', error);
          });
        }
        
        // Clear timer data
        sessionStorage.removeItem('completeTradeTimer');
        
        // Redirect to appointment trade page
        navigate('/appointment-swap', { 
          state: { 
            expired: true,
            message: 'Page refresh detected. Please try taking the appointment again.'
          } 
        });
        return; // Exit early to prevent timer initialization
      } catch (error) {
        console.error('❌ Error parsing saved timer data on refresh:', error);
        sessionStorage.removeItem('completeTradeTimer');
        refreshDetected.current = true;
        shouldClearSessionStorage.current = true;
        navigate('/appointment-swap');
        return; // Exit early to prevent timer initialization
      }
    } else {
      console.log('🔍 No saved timer data found - not a refresh');
    }
  }, []); // Empty dependency array to run only once on mount

  // Check if session still exists and redirect accordingly
  const checkSessionAndRedirect = async () => {
    if (!tradeOrder?.activeSessionId || !tradeOrder?.originalOrderId) {
      // No session to check, redirect to appointment trade page
      console.log('❌ No session ID or trade order, redirecting to appointment trade page');
      navigate('/appointment-swap');
      return false;
    }

    try {
      console.log('🔍 Checking session:', tradeOrder.activeSessionId);
      // Check if the session still exists
      const { doc, getDoc } = await import('firebase/firestore');
      const sessionRef = doc(db, 'activeBookings', tradeOrder.activeSessionId);
      const sessionDoc = await getDoc(sessionRef);

      if (sessionDoc.exists()) {
        // Our session exists, continue with timer
        console.log('✅ Our session still exists, continuing with timer');
        return true;
      } else {
        // Session doesn't exist, redirect to appointment trade page
        console.log('❌ Our session expired, redirecting to appointment trade page');
        navigate('/appointment-swap', { 
          state: { 
            expired: true,
            message: 'This appointment is no longer available. Please try taking another appointment.'
          } 
        });
        return false;
      }
    } catch (e) {
      console.error('⚠️ Error checking session:', e);
      // On error, redirect to appointment trade page as fallback
      navigate('/appointment-swap');
      return false;
    }
  };

  // Initialize timer for free appointments (only if no refresh detected)
  useEffect(() => {
    console.log('⏰ Timer initialization effect running...');
    console.log('⏰ authLoading:', authLoading);
    console.log('⏰ isTakingTrade:', isTakingTrade);
    console.log('⏰ tradeOrder:', tradeOrder);
    console.log('⏰ timeLeft:', timeLeft);
    console.log('⏰ timerActive:', timerActive);
    
    if (!authLoading && isTakingTrade && tradeOrder && !timeLeft && !timerActive && !refreshDetected.current) {
      // Check session first before starting timer
      checkSessionAndRedirect().then((sessionExists) => {
        if (!sessionExists) {
          // Session check failed, user was redirected
          return;
        }
        
        // Determine timer duration based on appointment type
        const isFreeAppointment = tradeOrder.appointmentType === 'free';
        const initialTime = 60; // 1 minute for both free and special appointments
        
        console.log(`⏰ Initializing ${initialTime}-second timer for ${isFreeAppointment ? 'free' : 'special'} appointment trade...`);
        
        // Check for existing timer in sessionStorage (should be empty after refresh detection)
        const savedTimer = sessionStorage.getItem('completeTradeTimer');
        console.log('⏰ Saved timer data in init:', savedTimer);
        
        if (savedTimer) {
          try {
            const timerData = JSON.parse(savedTimer);
            console.log('⏰ Restoring timer from sessionStorage:', timerData.timeLeft);
            setTimeLeft(timerData.timeLeft);
            setTimerActive(timerData.timerActive);
            timeLeftRef.current = timerData.timeLeft;
            return;
          } catch (error) {
            console.error('⏰ Error parsing saved timer data:', error);
            sessionStorage.removeItem('completeTradeTimer');
          }
        }
        
        // Initialize new timer
        setTimeLeft(initialTime);
        setTimerActive(true);
        timeLeftRef.current = initialTime;
        
        // Save to sessionStorage for persistence
        const timerData = {
          timeLeft: initialTime,
          timerActive: true,
          tradeOrder: tradeOrder
        };
        sessionStorage.setItem('completeTradeTimer', JSON.stringify(timerData));
        console.log(`⏰ Timer initialized for ${isFreeAppointment ? 'free' : 'special'} appointment trade:`, initialTime, 'seconds');
      });
    } else {
      console.log('⏰ Timer initialization conditions not met');
    }
  }, [authLoading, isTakingTrade, tradeOrder]);

  // Timer countdown effect
  useEffect(() => {
    let interval = null;
    
    if (timerActive && timeLeftRef.current > 0) {
      console.log('Starting timer with timeLeft:', timeLeftRef.current);
      interval = setInterval(() => {
        timeLeftRef.current = timeLeftRef.current - 1;
        setTimeLeft(timeLeftRef.current);
        
        // Update sessionStorage
        const timerData = {
          timeLeft: timeLeftRef.current,
          timerActive: true,
          tradeOrder: tradeOrder
        };
        sessionStorage.setItem('completeTradeTimer', JSON.stringify(timerData));
        
                 if (timeLeftRef.current <= 0) {
           setTimerActive(false);
           shouldClearSessionStorage.current = true;
           console.log('Timer expired, cleaning up session and redirecting to appointment trade page');
           
                       // Clean up active session when timer expires
            if (tradeOrder?.activeSessionId) {
              console.log('Timer expired - cleaning up active session');
              cleanupActiveSession(tradeOrder.activeSessionId, tradeOrder.originalOrderId).catch(error => {
                console.error('Error during timer expiration cleanup:', error);
              });
            }
           
           // Clear sessionStorage
           sessionStorage.removeItem('completeTradeTimer');
           
           // Redirect to appointment trade page
           navigate('/appointment-swap', { 
             state: { 
               expired: true,
               message: 'Trade time expired. The appointment is no longer available.'
             } 
           });
           return;
         }
      }, 1000);
    }
    
    return () => {
      if (interval) {
        console.log('Clearing timer interval');
        clearInterval(interval);
      }
    };
  }, [timerActive, navigate, tradeOrder]);

  // Cleanup timer data when component unmounts
  useEffect(() => {
    return () => {
      if (shouldClearSessionStorage.current) {
        console.log('Clearing completeTradeTimer on unmount');
        sessionStorage.removeItem('completeTradeTimer');
      }
    };
  }, []);

    // Handle navigation cleanup (component unmount only)
  useEffect(() => {
    return () => {
      // Always clean up session when component unmounts (user is leaving the page)
      // This ensures sessions are cleaned up regardless of how the user leaves
      if (tradeOrder?.activeSessionId) {
        console.log('Component unmounting - cleaning up active session (user leaving CompleteTrade page)');
        // Use a blocking cleanup to ensure session is deleted before user can navigate back
        cleanupActiveSession(tradeOrder.activeSessionId, tradeOrder?.originalOrderId).catch(error => {
          console.error('Error during unmount cleanup:', error);
        });
      }
      
      // Always clear timer data when navigating away
      console.log('Navigating away from CompleteTrade page - clearing timer data');
      sessionStorage.removeItem('completeTradeTimer');
      
      // Add a flag to prevent returning to the booking process
      sessionStorage.setItem('completeTradeSessionEnded', 'true');
    };
  }, [tradeOrder?.activeSessionId, tradeOrder?.originalOrderId]);



  const handleCompleteTrade = async () => {
    if (!currentUser || !tradeOrder) {
      setError('User not authenticated or trade order not found.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const totalPrice = tradeOrder.price;
      const isFreeOrder = totalPrice === 0;
      
      console.log(`🔄 Processing ${isFreeOrder ? 'free' : 'paid'} trade order:`, tradeOrder.originalOrderId);
      
      // First, transfer the order to get the new order ID
      const transferResult = await transferTradingOrderCallable({
        originalOrderId: tradeOrder.originalOrderId,
        paymentData: isFreeOrder ? {
          cardholderName: currentUser.displayName || currentUser.email || 'Free Trade Transfer'
        } : paymentData,
        
      });
      

      
      console.log(`✅ ${isFreeOrder ? 'Free' : 'Paid'} trade completed successfully:`, transferResult.data);
      
             // Clean up active session on successful completion
       await cleanupActiveSession(tradeOrder.activeSessionId, tradeOrder.originalOrderId);
      
      // Clear timer data on successful completion
      shouldClearSessionStorage.current = true;
      sessionStorage.removeItem('completeTradeTimer');
      
      alert(`${isFreeOrder ? 'Free' : 'Paid'} trade completed successfully! The appointment is now yours.`);
      navigate('/account');
      
    } catch (tradeError) {
      console.error('❌ Error processing trade:', tradeError);
      setError('Failed to complete the trade. Please try again.');
      
      // Handle specific error types from backend
      if (tradeError.code === 'functions/failed-precondition') {
        setError('This order is no longer available for trade. It may have been taken by someone else or is too close to the appointment time.');
      } else if (tradeError.code === 'functions/permission-denied') {
        setError('You cannot trade your own order.');
      } else if (tradeError.code === 'functions/not-found') {
        setError('The original order was not found. It may have been already taken.');
      } else {
        setError('There was an error processing the trade. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };



  const handleCancel = async () => {
    try {
      // Set flag to indicate user is leaving the booking process
      shouldClearSessionStorage.current = true;
      
      // Clean up active session when user cancels
      console.log('User cancelled trade - cleaning up session...');
      await cleanupActiveSession(tradeOrder?.activeSessionId, tradeOrder?.originalOrderId);
      console.log('Session cleanup completed');
      
      // Clear timer data when user cancels
      console.log('User cancelled trade - clearing timer data');
      sessionStorage.removeItem('completeTradeTimer');
      
      // Navigate after cleanup is complete
      navigate('/appointment-swap');
    } catch (error) {
      console.error('Error during cancel cleanup:', error);
      // Still navigate even if cleanup fails
      sessionStorage.removeItem('completeTradeTimer');
      navigate('/appointment-swap');
    }
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#f9fafb',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          textAlign: 'center',
          color: '#1f2937'
        }}>
          <p>Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show loading state if not authenticated (should redirect)
  if (!currentUser) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#f9fafb',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          textAlign: 'center',
          color: '#1f2937'
        }}>
          <p>Redirecting to sign in...</p>
        </div>
      </div>
    );
  }

  // Show error if no trade order data
  if (!isTakingTrade || !tradeOrder) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#f9fafb',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          textAlign: 'center',
          color: '#1f2937'
        }}>
          <p>No trade order found. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f9fafb',
      padding: '2rem'
    }}>
      <div style={{ 
        maxWidth: '600px', 
        margin: '0 auto',
        background: 'white',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ 
            color: '#1f2937', 
            marginBottom: '0.5rem',
            fontSize: '2rem'
          }}>
            Complete {tradeOrder.price === 0 ? 'Free' : 'Special'} Trade
          </h1>
          <div style={{ 
            background: tradeOrder.price === 0 ? '#10b981' : '#f97316', 
            color: 'white',
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            display: 'inline-block',
            fontSize: '0.9rem',
            fontWeight: 'bold'
          }}>
            {tradeOrder.price === 0 ? `🎉 ${getAppointmentTypeLabel('free')}` : '💰 Special Appointment'}
          </div>
        </div>

        {/* Timer Display */}
        {timerActive && timeLeft !== null && (
          <div style={{
            background: timeLeft <= 10 ? '#fee2e2' : timeLeft <= 20 ? '#fef3c7' : '#f0f9ff',
            border: timeLeft <= 10 ? '1px solid #f87171' : timeLeft <= 20 ? '1px solid #f59e0b' : '1px solid #0ea5e9',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '1.2rem',
              fontWeight: 'bold',
              color: timeLeft <= 10 ? '#dc2626' : timeLeft <= 20 ? '#d97706' : '#0c4a6e',
              marginBottom: '0.5rem'
            }}>
              Time Remaining: {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
            </div>
            <div style={{
              fontSize: '0.9rem',
              color: timeLeft <= 10 ? '#dc2626' : timeLeft <= 20 ? '#d97706' : '#0c4a6e'
            }}>
              {timeLeft <= 10 ? 'Hurry! Complete your trade!' : 'Please complete your trade within the time limit'}
            </div>
          </div>
        )}

        {/* Trade Order Summary */}
        <div style={{ 
          background: '#f8fafc',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          <h3 style={{ 
            color: '#1f2937', 
            marginBottom: '1rem',
            fontSize: '1.2rem'
          }}>
            Trade Order Summary
          </h3>
          
          <div style={{ marginBottom: '1rem' }}>
            <strong>Service:</strong> {tradeOrder.serviceName || 'Car Wash Service'}
          </div>
          
          <div style={{ marginBottom: '1rem' }}>
            <strong>Description:</strong> {tradeOrder.serviceDescription || 'Appointment Service'}
          </div>
          
          <div style={{ marginBottom: '1rem' }}>
            <strong>Appointment Type:</strong> {tradeOrder.appointmentType || 'Standard'}
          </div>
          
          <div style={{ marginBottom: '1rem' }}>
            <strong>Location:</strong> {tradeOrder.location || 'Not specified'}
          </div>
          
          <div style={{ marginBottom: '1rem' }}>
            <strong>Date:</strong> {tradeOrder.appointmentDate}
          </div>
          
          <div style={{ marginBottom: '1rem' }}>
            <strong>Time:</strong> {tradeOrder.appointmentTime}
          </div>
          
          <div style={{ 
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: tradeOrder.price === 0 ? '#10b981' : '#1f2937'
          }}>
            Price: ${tradeOrder.price} {tradeOrder.price === 0 ? '(Free)' : ''}
          </div>
        </div>



        {/* Important Notice */}
        <div style={{ 
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '2rem'
        }}>
          <p style={{ 
            margin: 0,
            color: '#92400e',
            fontSize: '0.9rem'
          }}>
            <strong>Important:</strong> Do not click "Take Appointment" unless certain because appointments can only be traded once.
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div style={{ 
            background: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem',
            color: '#991b1b'
          }}>
            {error}
          </div>
        )}

                <div style={{ textAlign: 'center' }}>
          <div style={{
            background: tradeOrder.price === 0 ? '#d1fae5' : '#fef3c7',
            border: tradeOrder.price === 0 ? '1px solid #10b981' : '1px solid #f59e0b',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '2rem'
          }}>
            <h3 style={{
              color: tradeOrder.price === 0 ? '#065f46' : '#92400e',
              marginBottom: '1rem',
              fontSize: '1.2rem',
              fontWeight: 'bold'
            }}>
              {tradeOrder.price === 0 ? '🎉 Free Trade!' : '💰 Paid Trade'}
            </h3>
            <p style={{
              color: tradeOrder.price === 0 ? '#065f46' : '#92400e',
              marginBottom: '1.5rem',
              fontSize: '1rem'
            }}>
              {tradeOrder.price === 0 
                ? 'Your trade total is $0. No payment required.'
                : `Your trade total is $${tradeOrder.price}. Payment will be collected at the location.`
              }
            </p>
          </div>
          
          <div style={{ 
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center'
          }}>
            <button
              onClick={handleCancel}
              disabled={loading}
              style={{
                padding: '0.75rem 1.5rem',
                border: '2px solid #6b7280',
                borderRadius: '8px',
                background: 'white',
                color: '#6b7280',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                opacity: loading ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            
            <button
              onClick={handleCompleteTrade}
              disabled={loading}
              style={{
                padding: '0.75rem 1.5rem',
                border: 'none',
                borderRadius: '8px',
                background: loading ? '#9ca3af' : (tradeOrder.price === 0 ? '#10b981' : '#f97316'),
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Completing Trade...' : 'Complete Booking'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompleteTrade; 